import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * ConfirmDialog — small centred popup that asks the user to confirm an action.
 *
 * Props
 *  - open:         show / hide
 *  - icon:         lucide icon shown in the header tile
 *  - title:        the question
 *  - description:  optional supporting line
 *  - confirmLabel / cancelLabel
 *  - onConfirm / onCancel
 */
const ConfirmDialog = ({
  open,
  icon: Icon,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef(null);

  // Escape closes; focus starts on the safe option
  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 font-['Inter']">
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="relative w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl shadow-purple-900/10"
      >
        {Icon && (
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
            <Icon className="h-6 w-6 text-white" />
          </div>
        )}
        <h3 id="confirm-dialog-title" className="font-['Manrope'] text-lg font-extrabold tracking-tight text-gray-900">
          {title}
        </h3>
        {description && <p className="mt-1.5 text-sm leading-relaxed text-gray-500">{description}</p>}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="h-11 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-sm font-semibold text-white shadow-md shadow-purple-600/20 transition-all hover:shadow-lg"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
